import React from "react"
import { useTranslation } from "react-i18next"

import { formatMoney, formatDate } from "helpers/formatters"
import { isFreshFinance } from "helpers/features"
import Information from "components/ui/InfoList"
import { DebtType } from "types"

type Props = {
  item: DebtType
}

const DebtSummaryComponent = ({ item }: Props) => {
  const { t } = useTranslation()

  const {
    total_unpaid,
    overdue_total_sum,
    payment_schedule,
    debt_card_balance,
  } = item
  const isOverdue = overdue_total_sum && overdue_total_sum > 0

  const summaries = isFreshFinance()
    ? [
        {
          label: t("features.debt.list.summary.principal.title"),
          value: formatMoney(debt_card_balance?.unpaid_principal_balance),
          visible: true,
        },
        {
          label: t("features.debt.list.summary.interest.title"),
          value: formatMoney(debt_card_balance?.interest),
          visible: true,
        },
        {
          label: t("features.debt.list.summary.penalty.title"),
          value: formatMoney(debt_card_balance?.penalty),
          visible: true,
        },
        {
          label: t("features.debt.list.summary.other.title"),
          value: formatMoney(debt_card_balance?.other_sum),
          visible:
            debt_card_balance?.other_sum && debt_card_balance?.other_sum !== 0,
        },
        {
          label: t("features.debt.list.summary.collection.title"),
          value: formatMoney(debt_card_balance?.collection_fee),
          visible: true,
        },
        {
          label: t("features.debt.list.summary.contract.title"),
          value: formatMoney(debt_card_balance?.contract_fee),
          visible: true,
        },
        {
          label: t("features.debt.list.summary.court.title"),
          value: formatMoney(debt_card_balance?.court_fee),
          visible: true,
        },
        {
          label: (
            <strong>{t("features.debt.list.summary.balance.title")}</strong>
          ),
          value: formatMoney(total_unpaid),
          visible: true,
        },
      ]
    : [
        {
          label: t("features.debt.list.summary.principal.title"),
          value: formatMoney(
            payment_schedule?.next_payment_schedule_invoice?.principal,
          ),
          visible: true,
        },
        {
          label: t("features.debt.list.summary.interest.title"),
          value: formatMoney(
            payment_schedule?.next_payment_schedule_invoice?.total_interest_sum,
          ),
          visible: true,
        },
        {
          label: t("features.debt.list.summary.handling_fee.title"),
          value: formatMoney(
            payment_schedule?.next_payment_schedule_invoice?.other_sum,
          ),
          visible: true,
        },
        {
          label: t("features.debt.list.summary.total.title", {
            date: formatDate(
              payment_schedule?.next_payment_schedule_invoice?.due_date,
            ),
          }),
          value: formatMoney(
            payment_schedule?.next_payment_schedule_invoice?.total_sum,
          ),
          visible: true,
        },
        {
          label: t("features.debt.list.summary.cumulative.title", {
            date: formatDate(
              payment_schedule?.next_payment_schedule_invoice?.due_date,
            ),
          }),
          value: formatMoney(
            payment_schedule?.last_payment_schedule_overdue_invoice?.cumulative,
          ),
          visible: isOverdue,
        },
      ]

  const items = summaries.filter((item) => item.value && item.visible)

  return <Information dense items={items} />
}

export default DebtSummaryComponent
